'use client';

import {
  ReactNode, MutableRefObject, RefObject, useEffect,
} from 'react';

interface FloatingNavBarProps {
  children: ReactNode;
  /** Content rendered above the navbar pill (e.g. career coaching chips). */
  above?: ReactNode;
  // Accept both RefObject and MutableRefObject coming from useRef initialised with null
  barRef?: RefObject<HTMLDivElement> | MutableRefObject<HTMLDivElement | null>;
  /** Background variant for the pill. Use 'surface-100' on pages with a grey background. Defaults to 'surface' (white). */
  pillBackground?: 'surface' | 'surface-100';
  /** When true, renders as an in-flow element instead of a fixed overlay. */
  inline?: boolean;
}

/**
 * @description Floating navigation bar displayed at the bottom of the job list content.
 * Acts as a pill-shaped container; content is provided by the caller to support
 * different configurations per screen context (e.g. candidate vs employer).
 * An optional `above` slot stacks additional UI directly above the pill.
 */
export const FloatingNavBar = ({
  children, above, barRef, pillBackground = 'surface', inline = false,
}: FloatingNavBarProps) => {
  // Signal presence so the main bottom nav can hide while a floating nav is active.
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('floating-nav-change', { detail: { visible: true } }));
    return () => {
      window.dispatchEvent(new CustomEvent('floating-nav-change', { detail: { visible: false } }));
    };
  }, []);

  if (inline) {
    return (
      <div className="flex w-full flex-col items-center gap-2 px-2 pb-1 pt-4 lg:px-0">
        {above}
        <div
          ref={barRef}
          className={`flex w-full items-center justify-center gap-4 rounded-2xl px-4 py-[14px] lg:w-auto lg:gap-6 lg:px-7 ${pillBackground === 'surface-100' ? 'bg-surface-100' : 'bg-surface'}`}
        >
          {children}
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-x-2 bottom-2 z-30 flex flex-col items-center gap-2 lg:bottom-8"
      style={{ left: 'calc(var(--two-column-header-offset, 0px) + 0.5rem)' }}
    >
      {above}
      <div
        ref={barRef}
        className={`flex w-full items-center justify-center gap-4 rounded-2xl px-4 py-[14px] lg:w-auto lg:gap-6 lg:px-7 ${pillBackground === 'surface-100' ? 'bg-surface-100' : 'bg-surface'}`}
      >
        {children}
      </div>
    </div>
  );
};
